import { useEffect, useState } from "react";
import { useT } from "../lib/i18n";
import type { EsperaKeeper } from "../hooks/useEsperaKeeper";

/**
 * El PIE DE TEXTO que acompaña al robot cuando la posición está fuera de rango. El robot en sí
 * camina por la barra REAL del rango (ver `Banda`, prop `reajustando`); esto explica en palabras
 * qué está haciendo. Se muestra en la ficha justo debajo de la banda cuando `!enRango`.
 *
 * DICE LA VERDAD. "Reajustando ahora…" solo aparece cuando el keeper está de verdad enviando la
 * transacción. Si espera a propósito —el precio pegado al borde, el precio medio sin confirmar, la
 * bolsa de gas vacía, el gas de la red caro…— lo cuenta con su motivo, desde cuándo y cuándo vuelve
 * a mirar. Sin eso, un robot que espera y un robot averiado se ven exactamente igual.
 *
 * Sin estado del keeper (red que no lo publica, fichero viejo o vault que no aparece) se dice lo
 * único que se sabe seguro: que lo revisará en breve.
 */

type Texto = { titulo: string; cuerpo?: string; espera?: boolean };

const TEXTOS: Record<string, Texto> = {
  // El keeper la vio dentro en su última ronda: el precio salió después.
  en_rango: {
    titulo: "El precio acaba de salir del rango",
    cuerpo: "el robot lo verá en su próxima revisión y moverá tu rango si sigue fuera.",
  },
  reajustando: {
    titulo: "Reajustando ahora…",
    cuerpo: "el robot está enviando la transacción que mueve tu rango a donde tu liquidez vuelve a cobrar.",
  },
  reajustado: {
    titulo: "Rango reajustado",
    cuerpo: "el robot acaba de mover tu rango; la ficha se pondrá al día en unos segundos.",
  },
  cerrando: {
    titulo: "Cerrando por objetivo de ganancia…",
    cuerpo: "tu piscina alcanzó el objetivo que marcaste y el robot la está liquidando.",
  },
  margen_recuperacion: {
    titulo: "El robot espera a que el precio se aleje del borde (protege tu capital)",
    cuerpo: "reajustar pegado al borde convertiría en definitiva una pérdida que todavía se puede recuperar.",
    espera: true,
  },
  twap: {
    titulo: "Esperando que el precio medio confirme la salida",
    cuerpo: "el robot no reajusta por un pico momentáneo: espera a que el precio medio (TWAP) también salga del rango.",
    espera: true,
  },
  tick_multiplo: {
    titulo: "Esperando un precio seguro para reajustar",
    cuerpo: "el precio está justo en un punto en el que reajustar perdería la recuperación; se reintenta en unos minutos.",
    espera: true,
  },
  cooldown: {
    titulo: "Pausa entre reajustes",
    cuerpo: "tu piscina se reajustó hace poco y el robot respeta el tiempo mínimo entre reajustes que fijaste.",
    espera: true,
  },
  sin_gas: {
    titulo: "Bolsa de gas sin saldo: recárgala para que el robot pueda reajustar",
    cuerpo: "el robot no adelanta gas que la bolsa no puede devolver. Recárgala desde la ficha (Bolsa de gas).",
    espera: true,
  },
  gas_caro: {
    titulo: "El robot está esperando a que baje el gas…",
    cuerpo: "reajustar con el gas caro puede costar diez veces más, y tu rango no cambia por esperar unos minutos.",
    espera: true,
  },
  sin_cuota: {
    titulo: "Sin reajustes disponibles",
    cuerpo: "la piscina agotó su cuota de reajustes; amplíala en Ajustes para que el robot pueda moverla.",
    espera: true,
  },
  pausado: {
    titulo: "Piscina en pausa",
    cuerpo: "el robot no la toca mientras esté pausada.",
    espera: true,
  },
  otro_operador: {
    titulo: "El robot de Keellabs no gestiona esta piscina",
    cuerpo: "tiene asignado otro operador.",
    espera: true,
  },
  reintento: {
    titulo: "El robot lo reintentará en la próxima revisión",
    cuerpo: "en el último intento el precio se movió o la simulación no pasó, así que no envió nada.",
    espera: true,
  },
};

const GENERICO: Texto = {
  titulo: "El robot revisará tu piscina en breve",
  cuerpo: "y moverá el rango a donde tu liquidez vuelva a cobrar si hace falta.",
};

function useAhora(cada = 30_000) {
  const [ahora, setAhora] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setAhora(Math.floor(Date.now() / 1000)), cada);
    return () => clearInterval(id);
  }, [cada]);
  return ahora;
}

export function RobotReajustando({ espera }: { espera?: EsperaKeeper | null }) {
  const t = useT();
  const ahora = useAhora();

  // "desde hace X min · próxima revisión en ~Y min": lo que distingue esperar de estar parado.
  const desdeMin = espera?.desde ? Math.max(0, Math.round((ahora - espera.desde) / 60)) : null;
  const faltaMin = espera?.proxima ? Math.round((espera.proxima - ahora) / 60) : null;
  const meta: string[] = [];
  if (desdeMin != null) {
    meta.push(desdeMin < 1 ? t("desde hace menos de un minuto") : t("desde hace {m} min", { m: String(desdeMin) }));
  }
  if (faltaMin != null) {
    meta.push(faltaMin >= 1
      ? t("próxima revisión del robot en ~{p} min", { p: String(faltaMin) })
      : t("próxima revisión del robot en breve"));
  }
  const pie = meta.length ? <span className="tenue">{meta.join(" · ")}</span> : null;

  if (espera?.motivo === "gas" && espera.gwei != null && espera.tope_s != null && espera.esperando_s != null) {
    const minutos = Math.round(espera.esperando_s / 60);
    const restan = Math.max(0, Math.round((espera.tope_s - espera.esperando_s) / 60));
    return (
      <p className="robot-reajuste-cap robot-espera" role="status" aria-live="polite">
        <b>{t("El robot está esperando a que baje el gas…")}</b>{" "}
        <span>
          {t("la red está a {g} gwei y reajusta por debajo de {u}. Lleva {m} min esperando; si no baja, reajusta igual en {r} min.", {
            g: espera.gwei.toFixed(3), u: String(espera.umbral ?? ""), m: String(minutos), r: String(restan),
          })}
        </span>{" "}
        <span className="tenue">
          {t("Reajustar con el gas caro puede costar diez veces más, y tu rango no cambia por esperar unos minutos.")}
        </span>
        {pie}
      </p>
    );
  }

  const txt = (espera && TEXTOS[espera.motivo === "gas" ? "gas_caro" : espera.motivo]) || GENERICO;
  return (
    <p className={`robot-reajuste-cap${txt.espera ? " robot-espera" : ""}`} role="status" aria-live="polite">
      <b>{t(txt.titulo)}</b>{" "}
      {txt.cuerpo && <span>{t(txt.cuerpo)}</span>}
      {/* Sin estado no hay ni "desde" ni "próxima": el genérico ya dice "en breve". */}
      {txt !== GENERICO && pie}
    </p>
  );
}
